/**
 * Lead file import (web Leads page → Import Leads → "CSV / Excel import").
 *
 * 2026-10-07 — rewritten after Mamta's ARCHEX Dehradun visitor sheet imported
 * as 86 name-only leads. The old importer lowercased each header, stripped
 * everything outside [a-z_] and read only 9 exact keys, so "CONTACT",
 * "ADDRESS" and two blank-header columns were silently dropped; it split rows
 * on ',' with no quote handling; and it tagged every row as IndiaMART.
 *
 * Everything here is pure (no React, no network) so the parsing, the header
 * aliases, the skip rules and the default source are unit-tested.
 */
import Papa from 'papaparse';

/** Lead fields a file column can feed. `ignore` drops the column. */
export type ImportField =
  | 'name' | 'phone' | 'company' | 'email' | 'location' | 'product'
  | 'quantity' | 'message' | 'source' | 'customerType' | 'notes' | 'ignore';

export const IMPORT_FIELD_LABELS: Record<ImportField, string> = {
  name: 'Name',
  phone: 'Phone',
  company: 'Company',
  email: 'Email',
  location: 'Location / city',
  product: 'Product',
  quantity: 'Quantity',
  message: 'Message / remarks',
  source: 'Lead source',
  customerType: 'Customer type (saved in message)',
  notes: 'Notes (saved in message)',
  ignore: 'Ignore this column',
};

/** Fields a column can be pointed at, in picker order. */
export const IMPORT_FIELD_OPTIONS: ImportField[] = [
  'name', 'phone', 'company', 'email', 'location', 'product', 'quantity',
  'message', 'customerType', 'notes', 'source', 'ignore',
];

/** Source label written when the file has no source column. */
export const DEFAULT_IMPORT_SOURCE = 'manual';

/** Header → field. Keys are normalised (lowercase, letters/digits only). */
const HEADER_ALIASES: Record<string, ImportField> = {};
function alias(field: ImportField, ...headers: string[]): void {
  for (const h of headers) HEADER_ALIASES[normaliseHeader(h)] = field;
}

/** "Phone No." → "phoneno", "NAME " → "name", "E-mail" → "email". */
export function normaliseHeader(header: string): string {
  return String(header ?? '').replace(/^﻿/, '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

alias('phone', 'phone', 'mobile', 'mobile no', 'mobile number', 'mobile no.', 'phone no', 'phone number',
  'contact', 'contact no', 'contact number', 'whatsapp', 'whatsapp no', 'whatsapp number', 'mob', 'mob no',
  'cell', 'cell no', 'phone/mobile', 'contact details');
alias('name', 'name', 'customer name', 'client name', 'contact person', 'full name', 'lead name', 'visitor name', 'person name');
alias('company', 'company', 'firm', 'firm name', 'business', 'business name', 'organisation', 'organization', 'company name');
alias('location', 'location', 'city', 'place', 'area', 'address', 'district', 'town', 'state');
alias('product', 'product', 'products', 'item', 'items', 'requirement product', 'product interested', 'product name');
alias('quantity', 'qty', 'quantity');
alias('email', 'email', 'e-mail', 'mail', 'email id', 'mail id');
alias('message', 'message', 'remarks', 'remark', 'notes', 'note', 'requirement', 'comment', 'comments');
alias('source', 'source', 'lead source', 'channel');
alias('customerType', 'customer type', 'type', 'category', 'profession', 'designation', 'visitor type');
alias('ignore', 'sr no', 'sr', 'sno', 's no', 'serial', 'serial no', 'sl no', 'no', 'index');

const PRODUCT_WORDS = /\b(tank|tanks|heat pump|pump|pipe|fitting|fittings|solar|geyser|heater|valve|purifier|ro)\b/i;
const CUSTOMER_TYPE_WORDS = /\b(end ?user|architect|architectural|interior|designer|builder|dealer|distributor|contractor|plumber|consumer|retailer|wholesaler|developer|engineer|owner)\b/i;

/**
 * Field for one column. A known header wins; a blank or unknown header is
 * guessed from its values (product words → Product, END USER / ARCHITECT /
 * DEALER… → Customer type, phone-shaped → Phone), else Ignore.
 */
export function guessFieldForColumn(header: string, values: string[]): ImportField {
  const key = normaliseHeader(header);
  const byHeader = key ? HEADER_ALIASES[key] : undefined;
  if (byHeader) return byHeader;
  const filled = values.map((v) => String(v ?? '').trim()).filter(Boolean);
  if (filled.length === 0) return 'ignore';
  const share = (re: RegExp) => filled.filter((v) => re.test(v)).length / filled.length;
  if (share(/^\+?[\d\s\-()/,]{10,}$/) >= 0.6) return 'phone';
  if (share(PRODUCT_WORDS) >= 0.5) return 'product';
  if (share(CUSTOMER_TYPE_WORDS) >= 0.5) return 'customerType';
  return 'ignore';
}

/** A file read into rows of cells. Row 0 is the header row. */
export type Table = string[][];

/** CSV text → table. Handles quoted fields, commas inside quotes, CRLF and a BOM. */
export function parseCsvText(text: string): Table {
  const clean = String(text ?? '').replace(/^﻿/, '');
  const res = Papa.parse<string[]>(clean, { skipEmptyLines: 'greedy' });
  return (res.data as string[][]).map((r) => r.map((c) => String(c ?? '')));
}

/** First sheet of an .xlsx/.xls workbook → table. `xlsx` is passed in so the bundle loads it lazily. */
export function parseWorkbook(
  XLSX: { read: (d: ArrayBuffer, o: { type: 'array' }) => any; utils: { sheet_to_json: (s: any, o: any) => unknown[] } },
  data: ArrayBuffer,
): Table {
  const wb = XLSX.read(data, { type: 'array' });
  const first = wb.SheetNames?.[0];
  if (!first) return [];
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[first], { header: 1, raw: false, defval: '', blankrows: false }) as unknown[][];
  return rows.map((r) => r.map((c) => String(c ?? '')));
}

export interface ImportColumn {
  index: number;
  header: string;
  /** What the preview shows for the column ("(blank column 5)" when unnamed). */
  label: string;
  samples: string[];
  field: ImportField;
}

/** Header row + first values → one ImportColumn per column, with a guessed field. */
export function buildColumns(table: Table): ImportColumn[] {
  if (table.length === 0) return [];
  const width = Math.max(...table.map((r) => r.length));
  const header = table[0];
  const body = table.slice(1);
  const cols: ImportColumn[] = [];
  const taken = new Set<ImportField>();
  for (let i = 0; i < width; i++) {
    const h = String(header[i] ?? '').trim();
    const values = body.map((r) => String(r[i] ?? '').trim());
    let field = guessFieldForColumn(h, values);
    // Single-value fields go to the first column that claims them; a second
    // "Phone" column is left for the user to point somewhere else.
    if (field !== 'ignore' && field !== 'message' && field !== 'notes' && field !== 'customerType' && taken.has(field)) {
      field = 'ignore';
    }
    if (field !== 'ignore') taken.add(field);
    cols.push({
      index: i,
      header: h,
      label: h || `(blank column ${i + 1})`,
      samples: Array.from(new Set(values.filter(Boolean))).slice(0, 3),
      field,
    });
  }
  return cols;
}

/** Every phone-shaped number in a cell, as last-10 digits for Indian numbers. */
export function extractPhones(cell: string): string[] {
  const parts = String(cell ?? '').split(/[/,;|]|\s{2,}|\bor\b|&/i);
  const out: string[] = [];
  // "9812345678 9812345679" (two numbers, one space) is one part with 20
  // digits — split it on whitespace instead of keeping only the last number.
  const pieces = parts.flatMap((p) => (p.replace(/\D/g, '').length > 13 ? p.split(/\s+/) : [p]));
  for (const p of pieces) {
    const d = p.replace(/\D/g, '');
    if (d.length < 10) continue;
    const l10 = d.slice(-10);
    if (!out.includes(l10)) out.push(l10);
  }
  return out;
}

export interface ImportLeadRow {
  sourceId: string;
  sourceName: string;
  name: string | null;
  company: string | null;
  email: string | null;
  phone: string;
  product: string | null;
  quantity: string | null;
  location: string | null;
  message: string | null;
  rawData: Record<string, string>;
}

export interface SkippedRow {
  line: number;
  name: string;
  reason: 'no_phone' | 'duplicate_in_file';
  value: string;
}

export interface ImportPlan {
  totalRows: number;
  rows: ImportLeadRow[];
  skipped: SkippedRow[];
  skippedNoPhone: number;
  skippedDuplicateInFile: number;
  usesDefaultSource: boolean;
}

/**
 * Table + column mapping → the rows to send. A row with no 10-digit phone is
 * skipped (phone is how AURA dedupes and how a rep calls back). A second
 * number in the phone cell goes into the message. Rows without a source
 * column are labelled Manual — never IndiaMART.
 */
export function buildImportPlan(table: Table, columns: ImportColumn[]): ImportPlan {
  const body = table.slice(1).filter((r) => r.some((c) => String(c ?? '').trim() !== ''));
  const rows: ImportLeadRow[] = [];
  const skipped: SkippedRow[] = [];
  const seen = new Set<string>();
  const hasSource = columns.some((c) => c.field === 'source');

  body.forEach((r, idx) => {
    const line = table.indexOf(r) + 1;
    const val = (f: ImportField): string[] =>
      columns.filter((c) => c.field === f).map((c) => String(r[c.index] ?? '').trim()).filter(Boolean);
    const first = (f: ImportField): string | null => val(f)[0] ?? null;

    const phoneCells = val('phone');
    const phones = phoneCells.flatMap(extractPhones);
    const name = first('name');
    if (phones.length === 0) {
      skipped.push({ line: line || idx + 2, name: name ?? '', reason: 'no_phone', value: phoneCells.join(' ') });
      return;
    }
    const [phone, ...otherPhones] = phones;
    if (seen.has(phone)) {
      skipped.push({ line: line || idx + 2, name: name ?? '', reason: 'duplicate_in_file', value: phone });
      return;
    }
    seen.add(phone);

    const messageParts = [
      ...val('message'),
      ...val('customerType').map((v) => `Customer type: ${v}`),
      ...val('notes'),
      ...(otherPhones.length ? [`Other number: ${otherPhones.join(', ')}`] : []),
    ];
    const rawData: Record<string, string> = {};
    columns.forEach((c) => {
      const v = String(r[c.index] ?? '').trim();
      if (v) rawData[c.label] = v;
    });
    const source = first('source');

    rows.push({
      sourceId: 'csv_import',
      sourceName: hasSource && source ? source : DEFAULT_IMPORT_SOURCE,
      name,
      company: first('company'),
      email: first('email'),
      phone,
      product: val('product').join(', ') || null,
      quantity: first('quantity'),
      location: val('location').join(', ') || null,
      message: messageParts.length ? messageParts.join(' | ') : null,
      rawData,
    });
  });

  return {
    totalRows: body.length,
    rows,
    skipped,
    skippedNoPhone: skipped.filter((s) => s.reason === 'no_phone').length,
    skippedDuplicateInFile: skipped.filter((s) => s.reason === 'duplicate_in_file').length,
    usesDefaultSource: !hasSource,
  };
}

/** Headers + example rows for "Download sample". Every header here is one the importer recognises. */
export const SAMPLE_HEADERS = ['Name', 'Mobile', 'Company', 'City', 'Product', 'Qty', 'Email', 'Source', 'Remarks'];

export function sampleCsv(): string {
  const rows = [
    SAMPLE_HEADERS,
    ['Ayush Jindal', '9915444977', 'Jindal Traders', 'Panipat', 'SS Water Tank', '2', 'ayush@example.com', 'Walk-in', '1000 L needed this month'],
    ['Site Engineer', '+91 90030 81477 / 9812345678', 'BGR Tech Ltd', 'Sector 33, Chandigarh', 'Heat Pump', '1', '', '', 'Commas are fine inside quotes'],
  ];
  return rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
}

function csvCell(v: string): string {
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
